/**
 * useMessageBuffer - Bounded message buffer for context panel
 */

import { useCallback, useMemo, useState } from 'react';
import { Message } from '@/types/api';

const DEFAULT_MAX_SIZE = 240;

const mergeMessages = (existing: Message[], incoming: Message[], mode: 'prepend' | 'append') => {
  if (incoming.length === 0) return existing;

  const seen = new Set(existing.map((msg) => msg.message_id));
  const filtered = incoming.filter((msg) => !seen.has(msg.message_id));

  return mode === 'prepend' ? [...filtered, ...existing] : [...existing, ...filtered];
};

const uniqueSorted = (messages: Message[]) => {
  const map = new Map<number, Message>();
  for (const msg of messages) {
    if (!map.has(msg.message_id)) {
      map.set(msg.message_id, msg);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.message_id - b.message_id);
};

export const useMessageBuffer = (maxSize: number = DEFAULT_MAX_SIZE) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasMoreBefore, setHasMoreBefore] = useState(true);
  const [hasMoreAfter, setHasMoreAfter] = useState(true);

  const reset = useCallback(
    (
      nextMessages: Message[],
      options?: { hasMoreBefore?: boolean; hasMoreAfter?: boolean }
    ) => {
      const sorted = uniqueSorted(nextMessages);
      setMessages(sorted);
      if (options?.hasMoreBefore !== undefined) {
        setHasMoreBefore(options.hasMoreBefore);
      }
      if (options?.hasMoreAfter !== undefined) {
        setHasMoreAfter(options.hasMoreAfter);
      }
    },
    []
  );

  const append = useCallback(
    (incoming: Message[], nextHasMoreAfter?: boolean) => {
      let trimmed = false;
      setMessages((prev) => {
        const merged = mergeMessages(prev, incoming, 'append');
        if (merged.length > maxSize) {
          trimmed = true;
          return merged.slice(merged.length - maxSize);
        }
        return merged;
      });
      if (trimmed) {
        setHasMoreBefore(true);
      }
      if (nextHasMoreAfter !== undefined) {
        setHasMoreAfter(nextHasMoreAfter);
      }
    },
    [maxSize]
  );

  const prepend = useCallback(
    (incoming: Message[], nextHasMoreBefore?: boolean) => {
      let trimmed = false;
      setMessages((prev) => {
        const merged = mergeMessages(prev, incoming, 'prepend');
        if (merged.length > maxSize) {
          trimmed = true;
          return merged.slice(0, maxSize);
        }
        return merged;
      });
      if (trimmed) {
        setHasMoreAfter(true);
      }
      if (nextHasMoreBefore !== undefined) {
        setHasMoreBefore(nextHasMoreBefore);
      }
    },
    [maxSize]
  );

  const minId = useMemo(() => (messages.length > 0 ? messages[0].message_id : null), [messages]);
  const maxId = useMemo(
    () => (messages.length > 0 ? messages[messages.length - 1].message_id : null),
    [messages]
  );

  return {
    messages,
    minId,
    maxId,
    hasMoreBefore,
    hasMoreAfter,
    reset,
    append,
    prepend,
  };
};
